/**
 * Trend Explanation Modal
 *
 * Transparent walkthrough of how the year-end projection is calculated for
 * a given PROA metric. Shows the historical seasonality curve, the expected
 * pace at the current point, and the final projection + classification.
 */

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatCurrency } from '@/lib/financial-utils'
import { TREND_STATUS_CONFIG, type TrendResult, type DREComponentKey } from '@/lib/services/trend-analysis.service'
import { SensitiveValue } from '@/components/privacy/SensitiveText'

const MONTH_ABBR_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const MONTH_NAMES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

interface TrendExplanationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Metric label, e.g. "Receita" or "Lucro Líquido" */
  metricLabel: string
  trend: TrendResult
}

export function TrendExplanationModal({
  open,
  onOpenChange,
  metricLabel,
  trend,
}: TrendExplanationModalProps) {
  const statusCfg = TREND_STATUS_CONFIG[trend.status]
  const projectedPct = trend.projectedPercent * 100
  const projectedPctLabel = Math.round(projectedPct).toLocaleString('pt-BR')
  const targetLabel = trend.vsYear ? `vs ${trend.vsYear}` : 'do PROA'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl bg-brand-dark-light border-brand-dark-lighter text-white"
        overlayClassName="bg-black/80"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-xl">
            Como a tendência de {metricLabel} é calculada
          </DialogTitle>
          <DialogDescription className="text-brand-gray-light">
            Projeção do fechamento do ano baseada no ritmo histórico da Monumenta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 text-sm mt-2">
          {trend.bottomUp ? (
            <BottomUpExplanation trend={trend} metricLabel={metricLabel} statusCfg={statusCfg} projectedPctLabel={projectedPctLabel} targetLabel={targetLabel} />
          ) : (
            <SeasonalityExplanation trend={trend} metricLabel={metricLabel} statusCfg={statusCfg} projectedPctLabel={projectedPctLabel} targetLabel={targetLabel} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Seasonality-based explanation (Receita, Faturamento)
// ============================================================================

function SeasonalityExplanation({
  trend,
  metricLabel,
  statusCfg,
  projectedPctLabel,
  targetLabel,
}: {
  trend: TrendResult
  metricLabel: string
  statusCfg: (typeof TREND_STATUS_CONFIG)[TrendResult['status']]
  projectedPctLabel: string
  targetLabel: string
}) {
  const currentMonthIdx = trend.currentMonth - 1
  const expectedPacePct = trend.expectedPaceRatio * 100
  const remainingPaceRatio = Math.max(0, 1 - trend.expectedPaceRatio)
  const remainingPacePct = remainingPaceRatio * 100
  const realVsProaPct = trend.proaAnnual > 0 ? (trend.realYTD / trend.proaAnnual) * 100 : 0
  const proaRestante = trend.proaAnnual * remainingPaceRatio

  const daysInCurrentMonth = new Date(trend.currentYear, trend.currentMonth, 0).getDate()
  const dayOfCurrentMonth = Math.max(1, Math.round(trend.monthProgress * daysInCurrentMonth))
  const isCurrentMonthPartial = trend.monthProgress > 0 && trend.monthProgress < 1
  const fullMonthIndices: number[] = []
  for (let m = 0; m < currentMonthIdx; m++) fullMonthIndices.push(m)
  if (!isCurrentMonthPartial && trend.monthProgress >= 1) {
    fullMonthIndices.push(currentMonthIdx)
  }
  const currentMonthPartialShare = isCurrentMonthPartial
    ? (trend.averageCurve[currentMonthIdx] ?? 0) * trend.monthProgress * 100
    : null

  return (
    <>
      {/* Step 1 */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-1">
          1. Base histórica
        </h3>
        <p className="text-brand-gray-light">
          Usamos o realizado mensal dos últimos{' '}
          <span className="text-white font-semibold">{trend.historicalYears.length} anos</span>{' '}
          ({trend.historicalYears.join(', ')}) para construir uma curva de sazonalidade.
          A curva captura naturalmente padrões como o pico de Promo no final do ano.
        </p>
      </section>

      {/* Step 2: seasonality table */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
          2. Curva de sazonalidade (% do total anual realizado em cada mês)
        </h3>
        <div className="border border-brand-dark-lighter rounded-lg overflow-hidden">
          <table className="w-full table-fixed text-[10px] sm:text-[11px]">
            <thead className="bg-brand-dark">
              <tr>
                <th className="w-12 text-left px-1.5 py-1.5 text-brand-gray font-medium">Ano</th>
                {MONTH_ABBR_PT.map((m, i) => (
                  <th
                    key={m}
                    className={`px-0.5 py-1.5 text-brand-gray font-medium text-center ${
                      i === currentMonthIdx ? 'bg-brand-primary/10 text-brand-primary' : ''
                    }`}
                  >
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trend.historicalYears.map((year) => (
                <tr key={year} className="border-t border-brand-dark-lighter">
                  <td className="px-1.5 py-1.5 font-medium text-white">{year}</td>
                  {trend.historicalCurves[year].map((v, i) => (
                    <td
                      key={i}
                      className={`px-0.5 py-1.5 text-center text-brand-gray-light tabular-nums ${
                        i === currentMonthIdx ? 'bg-brand-primary/5' : ''
                      }`}
                    >
                      {(v * 100).toFixed(1)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t-2 border-brand-dark-lighter bg-brand-dark/40">
                <td className="px-1.5 py-1.5 font-bold text-white">Média</td>
                {trend.averageCurve.map((v, i) => (
                  <td
                    key={i}
                    className={`px-0.5 py-1.5 text-center font-semibold tabular-nums ${
                      i === currentMonthIdx
                        ? 'bg-brand-primary/15 text-brand-primary'
                        : 'text-white'
                    }`}
                  >
                    {(v * 100).toFixed(1)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="bg-brand-dark px-2 py-1 text-[9px] text-brand-gray text-right">
            valores em %
          </div>
        </div>
      </section>

      {/* Step 3: expected pace */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
          3. Pace esperado até {dayOfCurrentMonth} de {trend.currentMonthLabel}
        </h3>
        <p className="text-brand-gray-light mb-2">
          Somamos a sazonalidade histórica dos meses já transcorridos:
        </p>
        <ul className="font-mono text-[13px] space-y-1 border border-brand-dark-lighter rounded-lg px-3 py-2 bg-brand-dark/40">
          {fullMonthIndices.map((m) => (
            <li key={m} className="flex items-center gap-2">
              <span className="text-brand-gray">•</span>
              <span className="w-24 text-brand-gray-light">{MONTH_NAMES_PT[m]}</span>
              <span className="text-brand-gray/70 text-[11px]">(mês completo)</span>
              <span className="ml-auto text-white tabular-nums">
                {((trend.averageCurve[m] ?? 0) * 100).toFixed(1)}%
              </span>
            </li>
          ))}
          {isCurrentMonthPartial && currentMonthPartialShare !== null && (
            <li className="flex items-center gap-2">
              <span className="text-brand-gray">•</span>
              <span className="w-24 text-brand-gray-light">{trend.currentMonthLabel}</span>
              <span className="text-brand-gray/70 text-[11px]">
                ({dayOfCurrentMonth} de {daysInCurrentMonth} dias —{' '}
                {Math.round(trend.monthProgress * 100)}% do mês)
              </span>
              <span className="ml-auto text-white tabular-nums">
                {currentMonthPartialShare.toFixed(1)}%
              </span>
            </li>
          )}
          <li className="flex items-center gap-2 pt-1 mt-1 border-t border-brand-dark-lighter">
            <span className="w-24 font-semibold text-white">Total</span>
            <span className="ml-auto font-bold text-brand-primary tabular-nums">
              {expectedPacePct.toFixed(1)}% do ano
            </span>
          </li>
        </ul>
        {isCurrentMonthPartial && (
          <p className="text-[11px] text-brand-gray mt-2 italic leading-relaxed">
            {trend.currentMonthLabel} ainda está em andamento — por isso usamos
            só a fração proporcional ({Math.round(trend.monthProgress * 100)}% da
            média histórica do mês), evitando contar um mês que não aconteceu inteiro.
          </p>
        )}
        <p className="text-xs text-brand-gray mt-2 italic">
          Historicamente, até esse ponto do ano a Monumenta realiza{' '}
          {expectedPacePct.toFixed(1)}% do total anual de {metricLabel.toLowerCase()}.
        </p>
      </section>

      {/* Step 4: realized */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-1">
          4. Realizado YTD
        </h3>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-xl font-bold font-display text-white">
            <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.realYTD)}</SensitiveValue>
          </span>
          <span className="text-brand-gray-light">
            = {realVsProaPct.toFixed(1)}% {trend.vsYear ? `do total de ${trend.vsYear}` : 'do PROA anual'} de{' '}
            <span className="text-white font-medium">
              <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.proaAnnual)}</SensitiveValue>
            </span>
          </span>
        </div>
      </section>

      {/* Step 5: projection */}
      <section className="bg-brand-dark/40 border border-brand-dark-lighter rounded-lg p-4">
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
          5. Projeção do fechamento
        </h3>
        {trend.vsYear ? (
          <>
            <p className="text-brand-gray-light mb-2">
              Extrapolamos o realizado YTD pela proporção histórica que
              esse período representa do ano completo (sazonalidade).
            </p>
            <div className="font-mono text-[13px] text-brand-gray-light space-y-1">
              <div>
                <span className="text-white">Realizado YTD / Pace esperado</span>
              </div>
              <div>
                ={' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.realYTD)}</SensitiveValue>
                {' '}/ {expectedPacePct.toFixed(1)}%
              </div>
              <div className="text-white text-base font-bold font-display pt-1">
                ={' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.projectedValue)}</SensitiveValue>
                {' '}
                <span className={statusCfg.color}>({projectedPctLabel}% vs {trend.vsYear})</span>
              </div>
            </div>
            <p className="text-[11px] text-brand-gray mt-3 italic leading-relaxed">
              Total de {trend.vsYear}:{' '}
              <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.proaAnnual)}</SensitiveValue>.
              A projeção indica crescimento de {projectedPctLabel}% em relação ao ano anterior.
            </p>
          </>
        ) : (
          <>
            <p className="text-brand-gray-light mb-2">
              O realizado é fato consumado. Para o restante do ano, assumimos
              que a Monumenta vai bater a fatia proporcional do PROA,
              respeitando a sazonalidade (ex: pico de Promo em Dezembro).
            </p>
            <div className="font-mono text-[13px] text-brand-gray-light space-y-1">
              <div>
                Realizado YTD +{' '}
                <span className="text-white">
                  ( PROA anual × sazonalidade restante )
                </span>
              </div>
              <div>
                ={' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.realYTD)}</SensitiveValue>
                {' '}+ ({' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.proaAnnual)}</SensitiveValue>
                {' '}× {remainingPacePct.toFixed(1)}% ){' '}
              </div>
              <div>
                ={' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.realYTD)}</SensitiveValue>
                {' '}+{' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(proaRestante)}</SensitiveValue>
              </div>
              <div className="text-white text-base font-bold font-display pt-1">
                ={' '}
                <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.projectedValue)}</SensitiveValue>
                {' '}
                <span className={statusCfg.color}>({projectedPctLabel}% do PROA)</span>
              </div>
            </div>
            <p className="text-[11px] text-brand-gray mt-3 italic leading-relaxed">
              Sazonalidade restante = 100% − {expectedPacePct.toFixed(1)}% (pace
              esperado até {trend.currentMonthLabel}) = {remainingPacePct.toFixed(1)}%.
              É a fatia do ano que historicamente ainda está por vir.
            </p>
          </>
        )}
      </section>

      <ClassificationSection statusCfg={statusCfg} projectedPctLabel={projectedPctLabel} targetLabel={targetLabel} stepNumber={6} />
    </>
  )
}

// ============================================================================
// Bottom-up explanation (Lucro Líquido)
// ============================================================================

const COMPONENT_LABELS: Record<DREComponentKey, string> = {
  receita: 'Receita',
  folha: 'Folha',
  terceiros: 'Terceiros',
  despesas: 'Despesas',
  impostos: 'Impostos',
  receitaFinanceira: 'Rec. Financeira',
}

const COMPONENT_ORDER: DREComponentKey[] = [
  'receita', 'folha', 'terceiros', 'despesas', 'impostos', 'receitaFinanceira',
]

function isSubtracted(key: DREComponentKey): boolean {
  return key !== 'receita' && key !== 'receitaFinanceira'
}

function BottomUpExplanation({
  trend,
  statusCfg,
  projectedPctLabel,
  targetLabel,
}: {
  trend: TrendResult
  metricLabel: string
  statusCfg: (typeof TREND_STATUS_CONFIG)[TrendResult['status']]
  projectedPctLabel: string
  targetLabel: string
}) {
  const cp = trend.componentProjections
  if (!cp) return null

  return (
    <>
      {/* Step 1 */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-1">
          1. Metodologia
        </h3>
        <p className="text-brand-gray-light">
          Projetamos cada linha da DRE individualmente usando a curva de
          sazonalidade histórica dos últimos{' '}
          <span className="text-white font-semibold">{trend.historicalYears.length} anos</span>{' '}
          ({trend.historicalYears.join(', ')}). A mesma metodologia usada para Receita
          é aplicada a cada componente de custo, garantindo estabilidade.
        </p>
      </section>

      {/* Step 2: component table */}
      <section>
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
          2. Projeção por componente (Dez/{trend.currentYear})
        </h3>
        <div className="border border-brand-dark-lighter rounded-lg overflow-hidden">
          <table className="w-full text-[11px] sm:text-xs">
            <thead className="bg-brand-dark">
              <tr>
                <th className="text-left px-3 py-2 text-brand-gray font-medium">Componente</th>
                <th className="text-right px-3 py-2 text-brand-gray font-medium">Realizado YTD</th>
                <th className="text-right px-3 py-2 text-brand-gray font-medium">PROA Anual</th>
                <th className="text-right px-3 py-2 text-brand-gray font-medium">Projetado</th>
                <th className="text-right px-3 py-2 text-brand-gray font-medium">% PROA</th>
              </tr>
            </thead>
            <tbody>
              {COMPONENT_ORDER.map((key) => {
                const c = cp[key]
                const sign = isSubtracted(key) ? '−' : ''
                return (
                  <tr key={key} className="border-t border-brand-dark-lighter">
                    <td className="px-3 py-2 font-medium text-white">{COMPONENT_LABELS[key]}</td>
                    <td className="px-3 py-2 text-right text-brand-gray-light tabular-nums">
                      <SensitiveValue mask="R$ ••••">
                        {sign}{formatCurrency(c.realYTD)}
                      </SensitiveValue>
                    </td>
                    <td className="px-3 py-2 text-right text-brand-gray-light tabular-nums">
                      <SensitiveValue mask="R$ ••••">
                        {sign}{formatCurrency(c.proaAnnual)}
                      </SensitiveValue>
                    </td>
                    <td className="px-3 py-2 text-right text-white font-semibold tabular-nums">
                      <SensitiveValue mask="R$ ••••">
                        {sign}{formatCurrency(c.projectedValue)}
                      </SensitiveValue>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={
                        c.projectedPercent >= 1
                          ? (isSubtracted(key) ? 'text-red-400' : 'text-brand-primary')
                          : 'text-brand-gray-light'
                      }>
                        {(c.projectedPercent * 100).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-brand-gray mt-2 italic leading-relaxed">
          Cada componente é projetado pela fórmula: Realizado YTD + PROA × sazonalidade restante.
        </p>
      </section>

      {/* Step 3: composition formula */}
      <section className="bg-brand-dark/40 border border-brand-dark-lighter rounded-lg p-4">
        <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
          3. Composição do Lucro projetado
        </h3>
        <div className="font-mono text-[13px] text-brand-gray-light space-y-1">
          <div className="text-white">
            Receita − Folha − Terceiros − Despesas − Impostos + Rec.Fin
          </div>
          <div>
            ={' '}
            <SensitiveValue mask="R$ ••••">{formatCurrency(cp.receita.projectedValue)}</SensitiveValue>
            {' '}− <SensitiveValue mask="R$ ••••">{formatCurrency(cp.folha.projectedValue)}</SensitiveValue>
            {' '}− <SensitiveValue mask="R$ ••••">{formatCurrency(cp.terceiros.projectedValue)}</SensitiveValue>
            {' '}− <SensitiveValue mask="R$ ••••">{formatCurrency(cp.despesas.projectedValue)}</SensitiveValue>
            {' '}− <SensitiveValue mask="R$ ••••">{formatCurrency(cp.impostos.projectedValue)}</SensitiveValue>
            {' '}+ <SensitiveValue mask="R$ ••••">{formatCurrency(cp.receitaFinanceira.projectedValue)}</SensitiveValue>
          </div>
          <div className="text-white text-base font-bold font-display pt-1">
            ={' '}
            <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.projectedValue)}</SensitiveValue>
            {' '}
            <span className={statusCfg.color}>({projectedPctLabel}% {targetLabel})</span>
          </div>
        </div>
        <p className="text-[11px] text-brand-gray mt-3 italic leading-relaxed">
          PROA anual de Lucro ={' '}
          <SensitiveValue mask="R$ ••••••">{formatCurrency(trend.proaAnnual)}</SensitiveValue>.
          A projeção atinge {projectedPctLabel}% dessa meta.
        </p>
      </section>

      <ClassificationSection statusCfg={statusCfg} projectedPctLabel={projectedPctLabel} targetLabel={targetLabel} stepNumber={4} />
    </>
  )
}

// ============================================================================
// Shared classification section
// ============================================================================

function ClassificationSection({
  statusCfg,
  projectedPctLabel,
  targetLabel,
  stepNumber,
}: {
  statusCfg: (typeof TREND_STATUS_CONFIG)[TrendResult['status']]
  projectedPctLabel: string
  targetLabel: string
  stepNumber: number
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2">
        {stepNumber}. Classificação
      </h3>
      <ul className="space-y-1 text-brand-gray-light">
        <li className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-primary" />
          <span className="font-medium text-white">Acima do ritmo</span> — projetado ≥ 105% {targetLabel}
        </li>
        <li className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-primary" />
          <span className="font-medium text-white">No ritmo</span> — projetado entre 97% e 104%
        </li>
        <li className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-yellow-400" />
          <span className="font-medium text-white">Em risco</span> — projetado entre 90% e 96%
        </li>
        <li className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="font-medium text-white">Abaixo</span> — projetado &lt; 90%
        </li>
      </ul>
      <div
        className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${statusCfg.bg} ${statusCfg.border}`}
      >
        <span className={`w-2 h-2 rounded-full ${statusCfg.dot}`} />
        <span className={`font-bold ${statusCfg.color}`}>
          Resultado: {statusCfg.label} ({projectedPctLabel}% projetado)
        </span>
      </div>
    </section>
  )
}
